"use client";

import { useRef } from "react";
import { FileSearch, Search, Crosshair, Settings, FileCheck, Compass, ShieldCheck, TrendingUp, BarChart3, ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
interface ServiceCard {
  icon: typeof FileSearch;
  title: string;
  description: string;
  linkText?: string;
  linkTo?: string;
}
const services: ServiceCard[] = [{
  icon: FileSearch,
  title: "B2B SEO Audits",
  description: "Thorough content, technical, and commercial reviews to uncover barriers to qualified enquiry generation.",
  linkText: "Explore SEO audit",
  linkTo: "/SeoAuditsPage"
}, {
  icon: Search,
  title: "Buyer-Intent Keyword Research",
  description: "Identify high-intent, non-branded queries mapped directly to your service capabilities and sales discussions."
}, {
  icon: Crosshair,
  title: "Competitor & Search Landscape Analysis",
  description: "Analyse competitor positioning, content gaps, and structural strengths to carve out clear market differentiation."
}, {
  icon: Settings,
  title: "Technical SEO",
  description: "Ensure improved crawlability and indexability, sound crawl paths, and an intuitive site structure for demanding enterprise buyers.",
  linkText: "Technical SEO services",
  linkTo: "/TechnicalSeoPage"
}, {
  icon: FileCheck,
  title: "Product & Service Page Optimisation",
  description: "Refine core commercial messaging and conversion touchpoints to address rigorous procurement evaluations."
}, {
  icon: Compass,
  title: "B2B Content Strategy",
  description: "Develop structured comparison guides and decision-stage content that support complex buyer committees."
}, {
  icon: ShieldCheck,
  title: "Authority & Search Visibility Development",
  description: "Build durable topical authority and credible industry citations through relevant, ethical search methods."
}, {
  icon: TrendingUp,
  title: "Conversion-Oriented Recommendations",
  description: "Optimise lead capture forms and strategic calls to action to turn high-intent traffic into qualified conversations."
}, {
  icon: BarChart3,
  title: "Reporting & Ongoing Optimisation",
  description: "Continuous refinements guided by Search Console, analytics data, and sales feedback to support pipeline contribution.",
  linkText: "Ongoing monthly SEO support",
  linkTo: "/MonthlySeoServicesPage"
}];
export default function Services() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".service-card", {
        y: 24
      });
      gsap.to(".service-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".service-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="b2bservicesgrid" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <p className="text-xs uppercase font-medium tracking-widest text-muted-foreground mb-3">
            SEO SERVICES
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground mb-4">
            B2B SEO services I provide
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-3">
            My B2B SEO services work as one integrated engagement, each element supporting the others, with no disconnected add-ons.
          </p>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Where geographic footprint or emergent generative engines matter to your pipeline,{" "}
            <Link to="/NationalSeoPage" className="text-foreground underline underline-offset-4 decoration-border hover:decoration-primary transition-colors">
              National SEO
            </Link>
            ,{" "}
            <Link to="/InternationalSeoPage" className="text-foreground underline underline-offset-4 decoration-border hover:decoration-primary transition-colors">
              International SEO
            </Link>
            , and{" "}
            <Link to="/AISearchOptimisationPage" className="text-foreground underline underline-offset-4 decoration-border hover:decoration-primary transition-colors">
              AI Search Optimisation
            </Link>{" "}
            can be layered directly into the strategy.
          </p>
        </div>

        {/* Services Grid */}
        <div ref={container} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map(item => {
          const Icon = item.icon;
          return <div key={item.title} className="service-card rounded-lg p-6 sm:p-8 bg-card border border-border flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <div>
                  <div className="w-12 h-12 rounded-md bg-secondary flex items-center justify-center text-primary mb-5">
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                    {item.description}
                  </p>
                </div>
                {item.linkText && item.linkTo && <div className="pt-2">
                    <Link to={item.linkTo} className="inline-flex items-center text-sm font-medium text-primary hover:underline group">
                      {item.linkText}
                      <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>}
              </div>;
        })}
        </div>

        {/* Closing Action Row */}
        <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-start gap-4">
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/ContactPage">Book a Free SEO Consultation</Link>
          </Button>
        </div>
      </div>
    </section>;
}